import domainEventWriteService from '../DomainEventWriteService'
import { com, kotlin } from '@eidu/web'
import createTrackingEvent = com.eidu.sharedlib.event.tracking.createTrackingEvent
import SetLogTypeEvent = com.eidu.sharedlib.event.eventlog.SetLogTypeEvent
import LogTypes = com.eidu.sharedlib.event.eventlog.LogTypes
import TrackingSenderKeys = com.eidu.sharedlib.event.tracking.TrackingSenderKeys
import KtMap = kotlin.collections.KtMap

const SENDER_APP_ID = 'entity_console'
const SENDER_APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '0.0.0'
const TRACKING_LOG_ID_KEY = 'TrackingService.trackingLogId'

type PostTrackingEventParams = {
  category: string
  action: string
  userEntityId: string
  organizationId: string
  assetId: string
  payload: Record<string, unknown>
}

export class TrackingService {
  private logIdPromisesByUser: Map<string, Promise<string>> = new Map()

  async postTrackingEvent({
    category,
    action,
    userEntityId,
    organizationId,
    assetId,
    payload,
  }: PostTrackingEventParams) {
    const event = createTrackingEvent(
      category,
      action,
      KtMap.fromJsMap(
        new Map<string, string>([
          [TrackingSenderKeys.APP_ID, SENDER_APP_ID],
          [TrackingSenderKeys.APP_VERSION, SENDER_APP_VERSION],
          [TrackingSenderKeys.USER_ID, userEntityId],
          [TrackingSenderKeys.ORGANIZATION_ID, organizationId],
          [TrackingSenderKeys.ASSET_ID, assetId],
        ])
      ),
      payload
    )

    const logId = await this.ensureTrackingLog(userEntityId)

    await domainEventWriteService.putEvent(logId, event)
  }

  private ensureTrackingLog(userEntityId: string): Promise<string> {
    const storageKey = `${TRACKING_LOG_ID_KEY}.${userEntityId}`
    const stored = localStorage.getItem(storageKey)
    if (stored) return Promise.resolve(stored)

    const pending = this.logIdPromisesByUser.get(userEntityId)
    if (pending) return pending

    const promise = this.createTrackingLog(storageKey).catch((e) => {
      this.logIdPromisesByUser.delete(userEntityId)
      throw e
    })
    this.logIdPromisesByUser.set(userEntityId, promise)
    return promise
  }

  private async createTrackingLog(storageKey: string): Promise<string> {
    const logId = crypto.randomUUID()
    await domainEventWriteService.putEvent(logId, new SetLogTypeEvent(LogTypes.TRACKING))
    localStorage.setItem(storageKey, logId)
    return logId
  }
}

const trackingService = new TrackingService()

export default trackingService
